import React, { useState, useEffect, useMemo } from 'react';
import { useOrganizations } from '../../hooks/useOrganizations';
import { useNotifications } from '../../hooks/useNotifications';
import { Organization } from '../../types/organization';
import { organizationService } from '../../services/organizationService';
import { 
  PlusIcon, 
  PencilSquareIcon, 
  TrashIcon, 
  BuildingOffice2Icon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import ConfirmDeleteModal from '../common/ConfirmDeleteModal';
import LoadingScreen from '../common/LoadingScreen';
import OrganizationSettings from './OrganizationSettings';

interface OrganizationFormData {
  name: string;
}

interface OrganizationListProps {
  // Props eltávolítva, mert most belső state-tel dolgozunk
}

const ITEMS_PER_PAGE = 10;

const OrganizationList: React.FC<OrganizationListProps> = () => {
  const { organizations, loading, refetch } = useOrganizations();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [formData, setFormData] = useState<OrganizationFormData>({ name: '' });
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [orgToDelete, setOrgToDelete] = useState<string | null>(null);
  const formRef = React.useRef<HTMLFormElement>(null);
  const { user } = useAuth();
  const { t } = useLanguage();
  const { createNotification } = useNotifications();

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<'all' | 'owner' | 'admin' | 'member'>('all');
  const [currentPage, setCurrentPage] = useState(1);

  // Filtered and paginated organizations
  const filteredOrganizations = useMemo(() => {
    let filtered = organizations;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(org =>
        org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        org.taxNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        org.address?.city?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Role filter
    if (filterRole !== 'all') {
      filtered = filtered.filter(org => {
        if (filterRole === 'owner') return org.ownerId === user?.id;
        if (filterRole === 'admin') return org.users?.find(u => u.userId === user?.id)?.role === 'admin';
        if (filterRole === 'member') return org.ownerId !== user?.id && !org.users?.find(u => u.userId === user?.id && u.role === 'admin');
        return true;
      });
    }

    return filtered;
  }, [organizations, searchTerm, filterRole, user?.id]);

  // Pagination
  const totalPages = Math.ceil(filteredOrganizations.length / ITEMS_PER_PAGE);
  const paginatedOrganizations = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredOrganizations.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredOrganizations, currentPage]);

  // Reset to page 1 when search/filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterRole]);

  const handleOpenCreateModal = () => {
    setEditingOrg(null);
    setFormData({ name: '' });
    setIsCreateModalOpen(true);
  };

  const handleOpenDetailsModal = (org: Organization) => {
    setSelectedOrg(org);
    setIsDetailsModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
    setEditingOrg(null);
    setFormData({ name: '' });
  };

  const handleCloseDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setSelectedOrg(null);
  };

  const handleOrganizationUpdate = (updatedOrg: Organization) => {
    setSelectedOrg(updatedOrg);
    refetch();
  };

  const handleSaveChanges = () => {
    // Trigger form submit in OrganizationSettings
    const formElement = document.querySelector('#org-settings-form') as HTMLFormElement;
    if (formElement) {
      formElement.requestSubmit();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingOrg) {
        const updatedOrg = {
          ...editingOrg,
          name: formData.name,
          updatedAt: new Date()
        };

        await organizationService.updateOrganization(editingOrg.id, updatedOrg);
        if (selectedOrg?.id === editingOrg.id) {
          setSelectedOrg(updatedOrg);
        }
        toast.success(t('common.messages.organization.updateSuccess'));

        // Értesítés létrehozása a szervezet tagjai számára
        try {
          const lang = t('common.language') === 'English' ? 'en' : 'hu';
          const translations = lang === 'en' 
            ? (await import('../../translations/en')).default.notifications
            : (await import('../../translations/hu')).default.notifications;
            
          const title = translations.subjects?.organizationUpdated 
            ? translations.subjects.organizationUpdated.replace('{{organizationName}}', formData.name)
            : `${lang === 'hu' ? 'Szervezet adatai frissültek' : 'Organization data updated'}: ${formData.name}`;
            
          const orgUsers = editingOrg.users || [];
          for (const orgUser of orgUsers) {
            await createNotification({
              type: 'system',
              title: title,
              message: `${lang === 'hu' ? 'A(z)' : 'The'} ${formData.name} ${lang === 'hu' ? 'szervezet adatai módosultak' : 'organization data has been updated'}.`,
              priority: 'low',
              userId: orgUser.userId,
              organizationId: editingOrg.id,
              createdBy: user?.id,
              createdByName: user?.displayName,
              actionUrl: '/settings'
            });
          }
        } catch (notifError) {
          console.error('Error creating notifications:', notifError);
        }
      } else {
        const newOrg = {
          name: formData.name,
          ownerId: user?.id || '',
          taxNumber: '',
          registrationNumber: '',
          address: {
            street: '',
            city: '',
            postalCode: '',
            country: ''
          },
          contact: {
            name: '',
            email: '',
            phone: '',
            position: ''
          },
          users: [],
          isActive: true,
          createdAt: new Date(),
          updatedAt: new Date()
        };

        const createdOrg = await organizationService.createOrganization(newOrg);
        toast.success(t('common.messages.organization.createdSuccess'));

        // Értesítés létrehozása a létrehozónak
        try {
          if (createdOrg && user?.id) {
            const lang = t('common.language') === 'English' ? 'en' : 'hu';
            const translations = lang === 'en' 
              ? (await import('../../translations/en')).default.notifications
              : (await import('../../translations/hu')).default.notifications;
              
            const title = translations.subjects?.organizationCreated 
              ? translations.subjects.organizationCreated.replace('{{organizationName}}', formData.name)
              : `${lang === 'hu' ? 'Új szervezet létrehozva' : 'New organization created'}: ${formData.name}`;
              
            await createNotification({
              type: 'system',
              title: title,
              message: `${lang === 'hu' ? 'Sikeresen létrehoztad a(z)' : 'You successfully created the'} ${formData.name} ${lang === 'hu' ? 'szervezetet' : 'organization'}.`,
              priority: 'medium',
              userId: user.id,
              organizationId: createdOrg.id,
              createdBy: user.id,
              createdByName: user.displayName,
              actionUrl: '/settings'
            });
          }
        } catch (notifError) {
          console.error('Error creating notification:', notifError);
        }
      }

      await refetch();
      handleCloseCreateModal();
    } catch (error) {
      console.error('Error submitting organization:', error);
      toast.error(t('common.messages.organization.createdError'));
    }
  };

  const handleDelete = async (orgId: string) => {
    setOrgToDelete(orgId);
    setIsConfirmDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (orgToDelete) {
      try {
        // Szervezet adatainak mentése értesítéshez
        const orgToDeleteData = organizations.find(org => org.id === orgToDelete);
        
        await organizationService.deleteOrganization(orgToDelete);
        await refetch();
        toast.success(t('common.messages.organization.deleteSuccess'));

        // Értesítés létrehozása a szervezet tagjai számára
        try {
          if (orgToDeleteData) {
            const lang = t('common.language') === 'English' ? 'en' : 'hu';
            const translations = lang === 'en' 
              ? (await import('../../translations/en')).default.notifications
              : (await import('../../translations/hu')).default.notifications;
              
            const title = translations.subjects?.organizationDeleted 
              ? translations.subjects.organizationDeleted.replace('{{organizationName}}', orgToDeleteData.name)
              : `${lang === 'hu' ? 'Szervezet törölve' : 'Organization deleted'}: ${orgToDeleteData.name}`;
              
            const orgUsers = orgToDeleteData.users || [];
            for (const orgUser of orgUsers) {
              await createNotification({
                type: 'system',
                title: title,
                message: `${lang === 'hu' ? 'A(z)' : 'The'} ${orgToDeleteData.name} ${lang === 'hu' ? 'szervezet törlésre került' : 'organization has been deleted'}.`,
                priority: 'high',
                userId: orgUser.userId,
                organizationId: orgToDelete,
                createdBy: user?.id,
                createdByName: user?.displayName
              });
            }
          }
        } catch (notifError) {
          console.error('Error creating notifications:', notifError);
        }
      } catch (error) {
        console.error('Error deleting organization:', error);
        toast.error(t('common.messages.organization.deleteError'));
      }
      setIsConfirmDeleteOpen(false);
      setOrgToDelete(null);
    }
  };

  const getRoleBadge = (org: Organization) => {
    if (org.ownerId === user?.id) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300">
          {t('organizations.roles.owner')}
        </span>
      );
    }
    if (org.users?.find(u => u.userId === user?.id)?.role === 'admin') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
          {t('organizations.roles.administrator')}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
        {t('organizations.roles.member')}
      </span>
    );
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="space-y-4">
      {/* Header with search and actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            {t('organizations.title')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {filteredOrganizations.length} {filteredOrganizations.length === 1 ? 'szervezet' : 'szervezet'}
          </p>
        </div>
        <button
          onClick={handleOpenCreateModal}
          className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors shadow-sm"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          {t('organizations.buttons.new')}
        </button>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Keresés név, adószám vagy város alapján..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Filter */}
          <div className="flex items-center gap-2">
            <FunnelIcon className="h-5 w-5 text-gray-400" />
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value as typeof filterRole)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">Összes szerep</option>
              <option value="owner">Tulajdonos</option>
              <option value="admin">Adminisztrátor</option>
              <option value="member">Tag</option>
            </select>
          </div>
        </div>
      </div>

      {/* Organizations Table/List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {paginatedOrganizations.length === 0 ? (
          <div className="text-center py-12">
            <BuildingOffice2Icon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
              Nincs találat
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Próbáljon meg más keresési feltételekkel.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Szervezet
                  </th>
                  <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Szerep
                  </th>
                  <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Adószám
                  </th>
                  <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Helyszín
                  </th>
                  <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Tagok
                  </th>
                  <th scope="col" className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Műveletek
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {paginatedOrganizations.map((org) => (
                  <tr
                    key={org.id}
                    onClick={() => handleOpenDetailsModal(org)}
                    className="cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50"
                  >
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8">
                          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary-500/10 to-primary-600/10 border border-primary-500/20 flex items-center justify-center">
                            <BuildingOffice2Icon className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                          </div>
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {org.name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      {getRoleBadge(org)}
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {org.taxNumber || '-'}
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {org.address?.city ? `${org.address.city}, ${org.address.country}` : '-'}
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {(org.users?.length || 0) + 1}
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDetailsModal(org);
                          }}
                          className="p-1.5 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                          title="Részletek"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(org.id);
                          }}
                          className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-white dark:bg-gray-800 px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Előző
            </button>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Következő
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                <span className="font-medium">{(currentPage - 1) * ITEMS_PER_PAGE + 1}</span>
                {' '}-{' '}
                <span className="font-medium">{Math.min(currentPage * ITEMS_PER_PAGE, filteredOrganizations.length)}</span>
                {' '}/ {' '}
                <span className="font-medium">{filteredOrganizations.length}</span>
                {' '}szervezet
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                      currentPage === i + 1
                        ? 'z-10 bg-primary-50 dark:bg-primary-900/30 border-primary-500 text-primary-600 dark:text-primary-400'
                        : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Modal for creating organization */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md shadow-xl">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {editingOrg ? t('organizations.buttons.edit') : t('organizations.buttons.new')}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('organizations.fields.name')}
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ name: e.target.value })}
                  className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm py-2 px-3 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseCreateModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                >
                  {t('organizations.buttons.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors shadow-sm"
                >
                  {editingOrg ? t('organizations.buttons.saveChanges') : t('organizations.buttons.create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Organization Details Modal */}
      {isDetailsModalOpen && selectedOrg && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-primary-500/10 to-primary-600/10 border border-primary-500/20">
                  <BuildingOffice2Icon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                    {selectedOrg.name}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Szervezet részletei</p>
                </div>
              </div>
              <button
                onClick={handleCloseDetailsModal}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <OrganizationSettings
                organization={selectedOrg}
                isEditing={true}
                onEditingChange={() => {}}
                onOrganizationUpdate={(updatedOrg) => {
                  handleOrganizationUpdate(updatedOrg);
                  handleCloseDetailsModal();
                }}
                hideButtons={true}
              />
            </div>
            {/* Sticky Footer with Action Buttons */}
            <div className="sticky bottom-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCloseDetailsModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Mégse
              </button>
              <button
                type="button"
                onClick={handleSaveChanges}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors shadow-sm"
              >
                Módosítások mentése
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete Modal */}
      <ConfirmDeleteModal
        isOpen={isConfirmDeleteOpen}
        onClose={() => {
          setIsConfirmDeleteOpen(false);
          setOrgToDelete(null);
        }}
        onConfirm={confirmDelete}
        title={t('organizations.messages.confirmTitle') || "Szervezet törlése"}
        deleteMessage={t('organizations.messages.deleteQuestion') || "Biztosan törölni szeretné ezt a szervezetet?"}
        deleteWarning={t('organizations.messages.deleteWarning') || "Ez a művelet nem visszavonható."}
      />
    </div>
  );
};

export default OrganizationList;
